/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HubLanding } from './pages/HubLanding';
import { CraReassessmentResponse } from './pages/CraReassessmentResponse';
import { Login } from './pages/Login';
import { Pricing } from './pages/Pricing';
import { Security } from './pages/Security';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { About } from './pages/About';
import { Contact } from './pages/Contact';
import { G72BasePrototype } from './pages/G72BasePrototype';
import { AuthGuard } from './components/AuthGuard';
import { AuthCallback } from './pages/AuthCallback';
import { NotFound } from './pages/NotFound';
import { VulnerabilityDisclosure } from './pages/VulnerabilityDisclosure';
import { Subprocessors } from './pages/Subprocessors';
import { MicrosoftClarity } from './components/analytics/MicrosoftClarity';

// All lazy-loaded modules use NAMED exports, so we map them to `default` for React.lazy.
// Without this, `React.lazy(() => import(x))` resolves to `{ default: undefined }` and
// crashes with React error #306 ("Element type is invalid ... got: undefined") at runtime.
const Chat = lazy(() => import('./pages/Chat').then(m => ({ default: m.Chat })));
const Session = lazy(() => import('./pages/Session').then(m => ({ default: m.Session })));
const SessionNew = lazy(() => import('./pages/SessionNew').then(m => ({ default: m.SessionNew })));
const G72ConnectedAppShell = lazy(() => import('./components/cowork/G72ConnectedAppShell').then(m => ({ default: m.G72ConnectedAppShell })));
const WorkspaceOverview = lazy(() => import('./pages/WorkspaceOverview').then(m => ({ default: m.WorkspaceOverview })));
const Settings = lazy(() => import('./components/Settings').then(m => ({ default: m.Settings })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));

export default function App() {
  return (
    <Router>
      <MicrosoftClarity />
      <Suspense fallback={<div className="flex items-center justify-center h-screen text-sm text-[var(--color-csai-muted)]">Loading...</div>}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HubLanding />} />
        <Route path="/cra-reassessment-response" element={<CraReassessmentResponse />} />
        <Route path="/accountants" element={<Navigate to="/" replace />} />
        <Route path="/ad/cra" element={<Navigate to="/cra-reassessment-response" replace />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/security" element={<Security />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/subprocessors" element={<Subprocessors />} />
        <Route path="/vulnerability-disclosure" element={<VulnerabilityDisclosure />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/prototype/g72-base" element={<G72BasePrototype />} />
        <Route path="/prototype/g72-base/*" element={<G72BasePrototype />} />

        {/* Authenticated app routes */}
        <Route path="/chat" element={<Navigate to="/app" replace />} />
        <Route path="/app" element={<AuthGuard><G72ConnectedAppShell /></AuthGuard>} />
        <Route path="/workspaces" element={<AuthGuard><WorkspaceOverview /></AuthGuard>} />
        <Route path="/chat-legacy" element={<AuthGuard><Chat /></AuthGuard>} />
        <Route path="/session/new" element={<AuthGuard><SessionNew /></AuthGuard>} />
        <Route path="/session/:id" element={<AuthGuard><Session /></AuthGuard>} />
        <Route path="/settings" element={<AuthGuard><Settings /></AuthGuard>} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </Router>
  );
}
